// SCR-STAFF-201 — جزئیات خدمت: تب عمومی (US-CAT-002)، زمان (US-CAT-010)، قیمت (US-CAT-020 تا 022، 025)، کد ملی و مالیات
// (US-CAT-003، 004)، اطلاعات عمومی و فاصله جلسات (US-CAT-006، 015)، منبع و صلاحیت (US-CAT-011، 012) و فرم‌ها و محدودیت‌ها
// (US-CAT-005، 013)؛ فعال‌سازی و غیرفعال‌سازی با نمایش اثر (US-CAT-007).
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { unwrap, type ServiceView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, DetailPage } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { catKeys, SensitiveBadge, ServiceStatusChip } from './common';
import { BomTab } from './ServiceBomTab';
import { GeneralTab } from './ServiceGeneralTab';
import { CodingTab } from './ServiceCodingTab';
import { FormsTab } from './ServiceFormsTab';
import { PriceTab } from './ServicePriceTab';
import { PublicTab } from './ServicePublicTab';
import { RequirementsTab } from './ServiceRequirementsTab';
import { TimeTab } from './ServiceTimeTab';

export const SCR_STAFF_201 = 'SCR-STAFF-201';

function StatusActions({ service }: { service: ServiceView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const [confirming, setConfirming] = useState(false);

  const impact = useQuery({
    queryKey: [...catKeys.service(session?.tenantId, service.id), 'impact'],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/services/{id}/deactivation-impact', {
          params: { path: { id: service.id } },
        }),
      ),
    enabled: confirming,
  });

  const change = useMutation({
    mutationFn: (action: 'activate' | 'deactivate') =>
      action === 'activate'
        ? unwrap(
            tenantApi.POST('/api/v1/catalog/services/{id}:activate', {
              params: { path: { id: service.id } },
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/catalog/services/{id}:deactivate', {
              params: { path: { id: service.id } },
            }),
          ),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
  });

  if (service.status === 'Active') {
    return (
      <>
        <Button
          color="warning"
          variant="outlined"
          disabled={!write.canWrite}
          onClick={() => setConfirming(true)}
          data-testid="service-deactivate"
        >
          {t('cat:services.deactivate')}
        </Button>
        <ConfirmDialog
          open={confirming}
          titleKey="cat:services.deactivate_title"
          messageKey={impact.data ? 'cat:services.deactivate_message' : 'common:states.loading'}
          messageValues={{ count: Number(impact.data?.total ?? 0) }}
          confirmKey="cat:services.deactivate"
          destructive
          busy={change.isPending || impact.isPending}
          error={change.error ?? impact.error}
          onConfirm={() => change.mutate('deactivate')}
          onCancel={() => setConfirming(false)}
        />
      </>
    );
  }

  return (
    <Button
      variant="contained"
      disabled={!write.canWrite || change.isPending}
      onClick={() => change.mutate('activate')}
      data-testid="service-activate"
    >
      {t('cat:services.activate')}
    </Button>
  );
}

/** `serviceId`: نمایش درون صفحه دیگر (پنل Setup Wizard)؛ وگرنه از مسیر `/settings/services/:id`. */
export function ServiceDetailPage({ serviceId }: { serviceId?: string } = {}) {
  const params = useParams();
  const id = serviceId ?? params['id'] ?? '';
  const { tenantApi, session } = useTenantSession();
  const [tab, setTab] = useState('general');
  const access = useAccess();
  // تب BOM فقط با ماژول انبار (feature.inv.inventory) و مجوز مشاهده کالا (US-CAT-014).
  const showBom = access.featureEnabled('feature.inv.inventory') && access.can('inv.item.view');

  const service = useQuery({
    queryKey: catKeys.service(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services/{id}', { params: { path: { id } } })),
  });

  const data = service.data;

  return (
    <DetailPage
      screenId={SCR_STAFF_201}
      titleKey="cat:detail.title"
      titleValues={{ name: data?.name ?? '' }}
      subtitleKey="cat:detail.subtitle"
      isLoading={service.isPending}
      error={service.error}
      onRetry={() => void service.refetch()}
      badges={
        data ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <ServiceStatusChip status={data.status} />
            {data.isSensitive && <SensitiveBadge />}
          </Stack>
        ) : undefined
      }
      actions={data ? <StatusActions service={data} /> : undefined}
      activeTab={tab}
      onTabChange={setTab}
      tabs={
        data
          ? [
              { id: 'general', labelKey: 'cat:detail.tabs.general', content: <GeneralTab service={data} /> },
              { id: 'time', labelKey: 'cat:detail.tabs.time', content: <TimeTab service={data} /> },
              { id: 'price', labelKey: 'cat:detail.tabs.price', content: <PriceTab service={data} /> },
              { id: 'coding', labelKey: 'cat:detail.tabs.coding', content: <CodingTab service={data} /> },
              { id: 'public', labelKey: 'cat:detail.tabs.public', content: <PublicTab service={data} /> },
              {
                id: 'requirements',
                labelKey: 'cat:detail.tabs.requirements',
                content: <RequirementsTab service={data} />,
              },
              { id: 'forms', labelKey: 'cat:detail.tabs.forms', content: <FormsTab service={data} /> },
              ...(showBom ? [{ id: 'bom', labelKey: 'cat:detail.tabs.bom', content: <BomTab service={data} /> }] : []),
            ]
          : []
      }
    />
  );
}
